// js/custom-element/section/product/variants.js
if (!customElements.get('product-variants')) {
  var ProductVariants = class extends CustomHTMLElement {
    async connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute('form-id')).id;
      this.optionSelectors = Array.from(this.querySelectorAll('[data-selector-type]'));
      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }
      this.product = await ProductLoader.load(this.productHandle);
      this.delegate.on('change', '[name^="option"]', this._onOptionChanged.bind(this));
      this.masterSelector.addEventListener('change', this._onMasterSelectorChanged.bind(this));
      this._updateDisableSelectors();
      this._updateOptionStatuses();
      this.selectVariant(this.selectedVariant['id']);
    }
    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }
    get productHandle() {
      return this.getAttribute('handle');
    }
    get hideSoldOutVariants() {
      return this.hasAttribute('hide-sold-out-variants');
    }
    get updateUrl() {
      return this.hasAttribute('update-url');
    }
    /**
     * Select a new variant by its ID
     */
    selectVariant(id) {
      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableOrSelectableVariant()['id'];
      }
      if (this.selectedVariant?.id === id) {
        return;
      }
      this.masterSelector.value = id;
      this.masterSelector.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set('variant', id);
        } else {
          newUrl.searchParams.delete('variant');
        }
        window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
      }
      this._updateDisableSelectors();
      this._updateOptionStatuses();
      triggerEvent(this.masterSelector.form, 'variant:changed', { variant: this.selectedVariant });
    }
    _onOptionChanged() {
      this.selectVariant(this._getVariantFromOptions()?.id);
    }
    _onMasterSelectorChanged() {
      const options = this.selectedVariant?.options || [];
      options.forEach((value, index) => {
        let input = this.querySelector(`input[name="option${index + 1}"][value="${CSS.escape(value)}"], select[name="option${index + 1}"]`),
          triggerChangeEvent = false;
        if (input.tagName === 'SELECT') {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === 'INPUT') {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }
        if (triggerChangeEvent) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    /**
     * Get the product variant by its ID
     */
    _getVariantById(id) {
      return this.product['variants'].find((variant) => variant['id'] === id);
    }
    /**
     * Get the variant based on the options
     */
    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();
      return this.product['variants'].find((variant) => {
        return variant['options'].every((value, index) => value === options[index]);
      });
    }
    /**
     * Detect if a specific variant is selectable. This is used when the "hide sold out variant" option is enabled, to allow
     * to return true only if the variant is actually available
     */
    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant['available'] || (!this.hideSoldOutVariants && !variant['available']);
      }
    }
    /**
     * This method is used internally to select an available or selectable variant, when the current choice does not
     * match the requirements. For instance, if sold out variants are configured to be hidden, but that the choices end
     * up being a non-valid variant, the theme automatically changes the variant to match the requirements. In the case
     * the customer end up on variant combinations that do not exist, it also switches to a valid combination.
     *
     * The algorithm is as follows: if we have for instance three options "Color", "Size" and "Material", we pop the last
     * option (Material) and try to find the first available variant for the given Color and Size. If none is found we
     * remove the second option (Size) and try to find the first available variant for the selected color. Finally, if none
     * is found we return the first available variant independently of any choice.
     */
    _getFirstMatchingAvailableOrSelectableVariant() {
      let options = this._getSelectedOptionValues(),
        matchedVariant = null,
        slicedCount = 0;
      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product['variants'].find((variant) => {
          if (this.hideSoldOutVariants) {
            return variant['available'] && variant['options'].slice(0, variant['options'].length - slicedCount).every((value, index) => value === options[index]);
          } else {
            return variant['options'].slice(0, variant['options'].length - slicedCount).every((value, index) => value === options[index]);
          }
        });
      } while (!matchedVariant && options.length > 0);
      return matchedVariant;
    }
    _getSelectedOptionValues() {
      const options = [];
      Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach((option) => options.push(option.value));
      return options;
    }
    /**
     * We add specific class to sold out variants based on the selectors
     */
    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;
      if (!selectedVariant) {
        return;
      }
      this._updateDisableSelectorsForOptionLevel(0, selectedVariant);
    }
    _updateDisableSelectorsForOptionLevel(level, selectedVariant) {
      if (!this.optionSelectors[level]) {
        return;
      }
      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        let selectorType = selector.getAttribute('data-selector-type'),
          cssSelector = '';
        switch (selectorType) {
          case 'swatch':
            cssSelector = `.color-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'variant-image':
            cssSelector = `.variant-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'block':
            cssSelector = `.block-swatch:nth-child(${valueIndex + 1})`;
            break;
          case 'dropdown':
            cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;
            break;
        }
        selector.querySelector(cssSelector).toggleAttribute('hidden', !hasAtLeastOneCombination);
        if (this.hideSoldOutVariants) {
          selector.querySelector(cssSelector).toggleAttribute('hidden', !available);
        } else {
          selector.querySelector(cssSelector).classList.toggle('is-disabled', !available);
        }
      };
      const hasCombination = (variant, level2, value, selectedVariant2) => {
        return Array.from({ length: level2 + 1 }, (_, i) => {
          if (i === level2) {
            return variant[`option${level2 + 1}`] === value;
          } else {
            return variant[`option${i + 1}`] === selectedVariant2[`option${i + 1}`];
          }
        }).every((condition) => condition);
      };
      this.product['options'][level]['values'].forEach((value, valueIndex) => {
        const hasAtLeastOneCombination = this.product['variants'].some((variant) => hasCombination(variant, level, value, selectedVariant) && variant);
        const hasAvailableVariant = this.product['variants'].some((variant) => hasCombination(variant, level, value, selectedVariant) && variant['available']);
        applyClassToSelector(this.optionSelectors[level], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
        this._updateDisableSelectorsForOptionLevel(level + 1, selectedVariant);
      });
    }
    /**
     * Update option status badges (e.g., "Ready to Ship", "Pre-Order") dynamically
     */
    _updateOptionStatuses() {
      // Check if variantOptionData is available
      if (typeof variantOptionData === 'undefined' || !variantOptionData) {
        return;
      }

      const selectedVariant = this.selectedVariant;
      if (!selectedVariant) {
        return;
      }

      // Get currently selected option values
      const selectedOptions = this._getSelectedOptionValues();

      // Loop through each option position
      this.optionSelectors.forEach((selector, optionIndex) => {
        const selectorType = selector.getAttribute('data-selector-type');

        // Only update dropdown selectors
        if (selectorType !== 'dropdown') {
          return;
        }

        const optionName = variantOptionData.options[optionIndex];
        if (!optionName) {
          return;
        }

        // Skip color/colour options
        const optionNameLower = optionName.toLowerCase();
        if (optionNameLower.includes('color') || optionNameLower.includes('colour')) {
          return;
        }

        // Get all option buttons in this selector
        const optionButtons = selector.querySelectorAll('.combo-box__option-item');

        optionButtons.forEach((button, valueIndex) => {
          const optionValue = button.getAttribute('value');

          // Find all variants that match:
          // 1. This option value
          // 2. All previously selected options (before this option position)
          // 3. Are available
          const matchingVariants = Object.values(variantOptionData.variants).filter(variant => {
            // Check if this variant has the current option value
            if (variant[`option${optionIndex + 1}`] !== optionValue) {
              return false;
            }

            // Check if this variant is available
            if (!variant.available) {
              return false;
            }

            // Check if this variant matches all previously selected options
            for (let i = 0; i < optionIndex; i++) {
              if (variant[`option${i + 1}`] !== selectedOptions[i]) {
                return false;
              }
            }

            return true;
          });

          // Collect unique statuses from matching variants
          const statuses = [...new Set(
            matchingVariants
              .map(v => v.status)
              .filter(status => status && status !== '' && status !== 'In Stock')
          )];

          // Determine which status to show
          let displayStatus = '';
          if (statuses.length > 0) {
            // Prioritize "Ready to Ship"
            if (statuses.includes('Ready to Ship')) {
              displayStatus = 'Ready to Ship';
            } else {
              // Use the first unique status
              displayStatus = statuses[0] ?? 'Sold Out';
            }
          } else {
            displayStatus = 'Sold Out';
          }

          // Update the status badge
          const statusBadge = button.querySelector('.combo-box__option-status');
          if (statusBadge) {
            if (displayStatus) {
              statusBadge.textContent = displayStatus;
              statusBadge.style.display = '';
            } else {
              statusBadge.textContent = '';
              statusBadge.style.display = 'none';
            }
          }
        });
      });
    }
  };
  window.customElements.define('product-variants', ProductVariants);
}
